<template>
    <div class="container-fluid" style="width: 820px;">

        <!--Quick Summary-->

        <div class="row">
            <div class="col-lg-3 col-md-6">
                <div class="d-flex align-items-center">
                    <div>
                        <h3>
                            <span class="label label-cyan" v-if="bill.status === 'UNPAID'">Unpaid</span>
                            <span class="label label-dark-green" v-if="bill.status === 'PAID'">Paid</span>
                        </h3>
                        <span>Status</span>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6">
                <div class="d-flex align-items-center">
                    <div>
                        <h3 class="font-medium m-b-0" v-if="bill.member_did && bill.member_catalog">
                            <did-details :catalog="bill.member_catalog" :did="bill.member_did"
                                         :show_blockie="true"></did-details>
                        </h3>
                        <h3 class="font-medium m-b-0" v-else>{{bill.buyer.biz_name}}</h3>
                        <span>Billed To</span>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6">
                <div class="d-flex align-items-center">
                    <div>
                        <h3 class="font-medium m-b-0">{{bill.amount_due | money}}</h3>
                        <span>Amount Due</span>
                    </div>
                </div>
            </div>
            <div class="col-lg-3 col-md-6 text-right">
                <div class="ml-auto">
                    <div>
                        <h3 class="font-medium text-right m-b-0">{{bill.due_at || "—" | medium}}</h3>
                        <span class="text-right">Due Date</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <div class="card card-shadow">
                    <div class="card-header">
                        <div class="d-md-flex align-items-center">
                            <div class="d-flex align-items-center">
                                <div>
                                    <h3 class="font-medium m-b-0">#{{bill.bill_number}}</h3>
                                    <span>Bill</span>
                                </div>
                            </div>

                            <div class="ml-auto m-t-10">
                                <div class="d-flex">
                                    <div class="m-r-20">
                                        <blockie :address="bill.seller.biz_email" :width="70"
                                                 v-if="!orgImg && bill.seller.biz_email"></blockie>
                                        <img :src="orgImg" class="rounded-circle" height="100" v-if="orgImg"
                                             width="100"/>
                                    </div>
                                    <address>
                                        <h3>{{bill.seller.name}}</h3>
                                        <p class="text-muted m-l-5">
                                            <i class="fas fa-phone"></i>&nbsp;{{bill.seller.biz_phone}}<br/>
                                            <i class="fas fa-envelope"></i>&nbsp;{{bill.seller.biz_email}}
                                        </p>
                                    </address>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr>
                    <div class="card card-body">
                        <div class="row">
                            <div class="col-md-6">
                                <div class="pull-left">
                                    <span>Bill To:</span>
                                    <h4 v-if="bill.member_did && bill.member_catalog">
                                        <did-details :catalog="bill.member_catalog" :did="bill.member_did"
                                                     :show_blockie="true"></did-details>
                                    </h4>
                                    <h3 v-else>{{bill.buyer.biz_name}}</h3>
                                    <p class="text-muted m-l-5" v-if="bill.buyer.id">
                                        {{bill.buyer.line1}}, &nbsp;{{bill.buyer.line2}}<br/>
                                        {{bill.buyer.country}},&nbsp;{{bill.buyer.city}}<br/>
                                        {{bill.buyer.state}},&nbsp;{{bill.buyer.postal_code}}<br/>
                                        <i class="fas fa-phone"></i>&nbsp;{{bill.buyer.biz_phone}}<br/>
                                        <i class="fas fa-envelope"></i>&nbsp;{{bill.buyer.biz_email}}
                                    </p>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="pull-right">
                                    <dl class="row mb-0">
                                        <div class="col-sm-6 text-sm-right">
                                            <dt>Creation Date</dt>
                                        </div>
                                        <div class="col-sm-6 text-sm-left">
                                            <dd class="mb-1">{{bill.created_at | date}}</dd>
                                        </div>
                                    </dl>
                                    <dl class="row mb-0">
                                        <div class="col-sm-6 text-sm-right">
                                            <dt>Issue Date</dt>
                                        </div>
                                        <div class="col-sm-6 text-sm-left">
                                            <dd class="mb-1">{{bill.issue_at | date}}</dd>
                                        </div>
                                    </dl>
                                    <dl class="row mb-0">
                                        <div class="col-sm-6 text-sm-right">
                                            <dt>Due Date</dt>
                                        </div>
                                        <div class="col-sm-6 text-sm-left">
                                            <dd class="mb-1">{{bill.due_at | date}}</dd>
                                        </div>
                                    </dl>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-12">
                                <div class="table-responsive m-t-40" style="clear: both;">
                                    <table class="table table-hover table-bordered">
                                        <thead>
                                        <tr>
                                            <th class="text-center">#</th>
                                            <th>Item Name</th>
                                            <th>Expense Category</th>
                                            <th>Quantity</th>
                                            <th>Rate</th>
                                            <th>Detail</th>
                                            <th class="text-right">Amount</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <template v-for="(item, index) in bill.bill_items">
                                            <tr v-bind:key="'ini_'+index">
                                                <td :rowspan="item.taxes.length>0? item.taxes.length+2:null"
                                                    class="text-center">{{index + 1}}
                                                </td>
                                                <td :rowspan="item.taxes.length>0? item.taxes.length+2:null">
                                                    {{item.name}}
                                                </td>
                                                <td :rowspan="item.taxes.length>0? item.taxes.length+2:null">
                                                    {{item.expense_category}}
                                                </td>
                                                <td :rowspan="item.taxes.length>0? item.taxes.length+2:null">
                                                    {{item.quantity | units}}
                                                </td>
                                                <td :rowspan="item.taxes.length>0? item.taxes.length+2:null">
                                                    {{item.amount.currency_symbol}}{{item.rate}}/{{item.quantity.unitSymbol}}
                                                </td>
                                                <td>Price</td>
                                                <td class="text-right">{{item.amount | money}}</td>
                                            </tr>
                                            <tr v-bind:key="'ini_'+index+'_tax_'+t_index"
                                                v-for="(tax, t_index) in item.taxes"
                                                v-if="item.taxes.length>0">
                                                <td>{{tax.name}} ({{tax.rate | unitFix(2)}}%)</td>
                                                <td class="text-right">{{tax.value | money}}</td>
                                            </tr>
                                            <tr class="font-weight-bold" v-if="item.taxes.length>0">
                                                <td>Sub Total</td>
                                                <td class="text-right"> {{getSubTotal(item) | money}}</td>
                                            </tr>
                                        </template>

                                        <tr class="font-weight-bold">
                                            <td class="b-t-0" colspan="5">
                                            <td>Total</td>
                                            <td class="text-right"> {{bill.total | money}}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div class="col-md-12">
                                <div class="pull-right m-t-30 text-right">
                                    <h4>Total: {{bill.total | money}}</h4>
                                </div>
                                <div class="clearfix"></div>
                            </div>
                        </div>
                    </div>
                    <div class="card-footer text-right">
                        <div class="button-group">
                            <button class="btn btn-info" data-target="#myModal" data-toggle="modal"
                                    v-if="bill.status === 'UNPAID'">Record Payment
                            </button>
                            <router-link :to="{name: 'edit-marketplace-bill'}" class="btn btn-primary" href="/express/1/challans/15/edit"
                               v-if="bill.status === 'UNPAID'"> Edit</router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div aria-hidden="true" aria-labelledby="myModalLabel" class="modal fade" id="myModal"
             role="dialog" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <form @submit.prevent="handleSubmit">
                        <div class="modal-header">
                            <h4 class="modal-title" id="myModalLabel">Record Payment</h4>
                            <button aria-hidden="true" class="close" data-dismiss="modal"
                                    type="button">×
                            </button>
                        </div>
                        <div class="modal-body card-blue">
                            <div class="card-body">
                                <div class="form-group row">
                                    <label class="col-sm-3 text-right control-label col-form-label"
                                           for="method">Payment Type</label>
                                    <div class="col-sm-7">
                                        <select :class="{'is-invalid': submitted && errors.has('method')}"
                                                class="custom-select mr-sm-2"
                                                data-vv-as="method"
                                                id="method"
                                                name="method"
                                                v-model="payment.method"
                                                v-validate="'required'">
                                            <option :value="null">Select Payment Method</option>
                                            <option value="BANK_TRANSFER">Bank Transfer</option>
                                            <option value="CHEQUE">Cheque</option>
                                            <option value="CASH">Cash</option>
                                        </select>
                                        <div class="invalid-feedback"
                                             v-if="submitted && errors.has('method')">{{
                                            errors.first('method') }}
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-sm-3 text-right control-label col-form-label"
                                           for="amount">Amount</label>
                                    <div class="col-sm-7">
                                        <input class="form-control"
                                               disabled
                                               id="amount"
                                               name="amount"
                                               step="any"
                                               type="number"
                                               v-model="bill.total.amount">
                                        <div class="invalid-feedback"
                                             v-if="submitted && errors.has('amount')">{{
                                            errors.first('amount') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group row">
                                    <label class="col-sm-3 text-right control-label col-form-label"
                                           for="notes">Payment Date</label>
                                    <div class="col-sm-7 input-group mb-3">
                                        <div class="input-group-prepend">
                                                                <span class="input-group-text"><i
                                                                        class="far fa-calendar-alt"></i></span>
                                        </div>
                                        <input :class="{'is-invalid': submitted && errors.has('payment_date')}"
                                               autocomplete="off"
                                               class="form-control"
                                               data-vv-as="payment_date"
                                               id="payment_date"
                                               name="payment_date"
                                               placeholder="mm/dd/yyyy"
                                               style="height: 42px;"
                                               type="text"
                                               v-model="payment.payment_date"
                                               v-validate="'required'">
                                        <div class="invalid-feedback"
                                             v-if="submitted && errors.has('payment_date')">{{
                                            errors.first('payment_date') }}
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group row">
                                    <label class="col-sm-3 text-right control-label col-form-label"
                                           for="notes">Notes</label>
                                    <div class="col-sm-7">
                                                            <textarea class="form-control"
                                                                      data-vv-as="amount"
                                                                      id="notes"
                                                                      name="notes"
                                                                      v-model="payment.notes">
                                                            </textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button class="btn waves-effect waves-light btn-success" type="submit">
                                Submit
                            </button>
                            <button @click="reset()" class="btn waves-effect waves-light btn-dark"
                                    type="button">Reset
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from "vuex";
    import Blockie from '../../../common/Blockie';
    import CurrencySelect from '../../../common/CurrencySelect';
    import DidDetails from "../../../common/DidDetails";

    export default {
        name: "View",
        components: {DidDetails, Blockie, CurrencySelect},
        computed: {
            ...mapGetters(["currentUser", "currentOrg"])
        },
        props: ["billid"],
        data() {
            return {
                submitted: false,
                bill: {
                    id: null,
                    partner_id: null,
                    total: {},
                    buyer: {},
                    seller: {},
                    notes: null,
                    expense_props: {},
                    attachments: null,
                    created_at: null,
                    issue_at: null,
                    due_at: null,
                    status: null,
                    bill_number: null,
                    bill_items: [],
                    amount_due: {}
                },
                orgImg: null,
                payment: {
                    method: null,
                    amount: null,
                    payment_date: null,
                    notes: null
                },
            }
        },
        mounted() {
            this.init();
        },
        methods: {
            async init() {
                jQuery('#payment_date').datepicker({
                    constrainInput: true,
                    autoclose: true,
                    todayHighlight: true
                }).on('change', (event) => {
                    this.payment.payment_date = event.target.value;
                });

                await this.getBill();
            },
            async getBill() {
                try {
                    $(".preloader").fadeIn();
                    let reply = await this.$http.get(`/marketplaces/${this.currentOrg.marketplace_id}/bills/${this.billid}`);
                    $(".preloader").fadeOut();
                    this.bill = reply.data;
                } catch (e) {
                    $(".preloader").fadeOut();
                }
            },
            getSubTotal(bill_item) {
                let amount = bill_item.amount.amount;
                let taxes = _.sumBy(bill_item.taxes, function (o) {
                    return o.value.amount;
                });

                amount = amount + taxes;

                this.payment.amount = amount;

                return {
                    amount: amount,
                    currency_id: this.bill.total.currency_id,
                    currency_name: this.bill.total.currency_name,
                    currency_symbol: this.bill.total.currency_symbol
                };
            },
            async handleSubmit(e) {
                this.submitted = true;
                let valid = await this.$validator.validate();
                if (valid) {
                    await this.recordPayment();
                }
            },
            async recordPayment() {
                try {
                    $(".preloader").fadeIn();
                    await this.$http.post(`/marketplaces/${this.currentOrg.marketplace_id}/bills/${this.billid}/record_payment`, this.payment);
                    $(".preloader").fadeOut();
                    this.$toastr('success', 'Payment recorded', 'Success');
                    this.reset();
                    await this.getBill();
                    $('#myModal').modal('toggle');
                } catch (e) {
                    $(".preloader").fadeOut();
                    console.log(e);
                    this.$toastr('error', e.response.data.reason, 'Failed');
                }
            },
            reset() {
                this.submitted = false;
                this.payment = {
                    method: null,
                    amount: null,
                    notes: null
                };
            }
        }
    }
</script>

<style scoped>

</style>
